// Database type definitions for IndexedDB persistence

// Re-export base types
export type { Manuscript, ManuscriptType, Series, Collection, LibraryData } from './manuscript';

// Planner structure types
export interface StructureItem {
  id: string;
  title?: string;
  type: "act" | "chapter" | "scene";
  children?: StructureItem[];
  metadata?: {
    pov?: string;
    povCharacterIds?: string[];
    tense?: "past" | "present";
    status?: "draft" | "revision" | "complete";
    notes?: string;
  };
}

export interface PlannerStructure {
  id: string;
  structure: StructureItem[];
}

// Codex types
export type CodexSection = "characters" | "locations" | "lore" | "plotlines";

export interface CodexEntry {
  id: string;
  manuscriptId: string;
  section: CodexSection;
  name: string;
  aliases?: string;
  description?: string;
}

// Goals types
export type DayType = "write" | "sprint" | "rest";

export interface WeekPattern {
  sunday: DayType;
  monday: DayType;
  tuesday: DayType;
  wednesday: DayType;
  thursday: DayType;
  friday: DayType;
  saturday: DayType;
}

export interface DailyProgressEntry {
  start: number;
  latest: number;
}

export interface Goals {
  id: string;
  totalGoal: number;
  wordsExcluded: number;
  targetDate: string;
  weekPattern: WeekPattern;
  dailyProgress: Record<string, DailyProgressEntry>;
}

// Format settings types
export type FontFamily = "serif" | "sans" | "mono";
export type LineHeight = "compact" | "normal" | "relaxed";
export type Margin = "narrow" | "normal" | "wide";
export type ParagraphStyle = "indented" | "block" | "book";

export interface FormatSettings {
  id: string;
  fontFamily: FontFamily;
  lineHeight: LineHeight;
  margin: Margin;
  paragraphStyle: ParagraphStyle;
}

// Scene content type
export interface SceneContent {
  id: string;
  manuscriptId: string;
  content: string;
}

// Durable sync outbox record
export interface OutboxEntry {
  id: string;
  manuscriptId: string;
  generation: number;
  updatedAt: string;
  isTombstone?: boolean;
  deletedAt?: string;
  expectedRevision?: number;
  purgeCloud?: boolean;
}

// Database schema version
export const DB_NAME = "deckle-db";
export const DB_VERSION = 4;

// Store names
export const STORES = {
  MANUSCRIPTS: "manuscripts",
  SERIES: "series",
  COLLECTIONS: "collections",
  PLANNER_STRUCTURES: "plannerStructures",
  CODEX_ENTRIES: "codexEntries",
  GOALS: "goals",
  FORMAT_SETTINGS: "formatSettings",
  ENCRYPTION_SETTINGS: "encryptionSettings",
  SCENE_CONTENT: "sceneContent",
  SYNC_OUTBOX: "syncOutbox",
} as const;
